/**
 * DocFormManager.js  — AMOS 문서 전달 관리 (리팩토링)
 *
 * 변경 사항:
 *   - CropTool         → imageUtils.js 공통 컴포넌트 사용
 *   - 이미지 뷰어 모달 → ImageViewer 컴포넌트 사용
 *   - 이미지 로드 로직 → useImageCache 훅 사용
 *   - ImageUploadBox   → 업로드 UI 공통 컴포넌트 사용
 *   - 기능·API 변경 없음
 */


// ── PDF 다운로드 ──────────────────────────────────────────────
async function downloadAllPdf(imgDataList, filename) {
  const validImgs = imgDataList.filter(Boolean);
  if (!validImgs.length) { alert('이미지가 없습니다'); return; }
  if (!window.jspdf) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  const { jsPDF } = window.jspdf;
  let pdf = null;
  for (const imgData of validImgs) {
    const img = new Image();
    img.src = imgData;
    await new Promise(r => { img.onload = r; });
    const isLandscape = img.width > img.height;
    if (!pdf) {
      pdf = new jsPDF({ orientation: isLandscape ? 'landscape' : 'portrait', unit: 'px', format: [img.width, img.height] });
    } else {
      pdf.addPage([img.width, img.height], isLandscape ? 'landscape' : 'portrait');
    }
    pdf.addImage(imgData, 'WEBP', 0, 0, img.width, img.height);
  }
  pdf.save(filename + '.pdf');
}

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CropTool, ImageViewer, ImageUploadBox, useImageCache } from './imageUtils';

const API_BASE       = 'https://adria.terrier-ule.ts.net';
const GRID_SIZE      = 8;
const GUIDE_SNAP     = 6;

// ══════════════════════════════════════════════════════════════
// 필드 에디터 (캔버스 기반 — 크롭툴 외 변경 없음)
// ══════════════════════════════════════════════════════════════
function FieldEditor({ templateId, imageData, allPages, initialFields, onSaved, onClose, authH }) {
  const [activePage, setActivePage] = React.useState(0);
  const currentImage = (allPages && allPages.length > activePage) ? allPages[activePage] : imageData;
  const canvasRef      = useRef(null);
  const imgRef         = useRef(null);
  const imgSizeRef     = useRef({ w: 1, h: 1 });
  const displaySizeRef = useRef({ w: 1, h: 1 });
  const [fields,   setFields]   = useState(initialFields || []);
  const [sel,      setSel]      = useState(null);
  const [snapOn,   setSnapOn]   = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [saveMsg,  setSaveMsg]  = useState('');
  const [guides,   setGuides]   = useState([]);
  const drag = useRef(null);
  const [hoverFid,   setHoverFid]   = useState(null);
  const [pendingType, setPendingType] = useState(null);
  const pendingTypeRef = useRef(null);
  const mousePos = useRef({ cx: 0, cy: 0 });
  const rafRef   = useRef(null);

  const nextId = () => `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  const snap   = useCallback((v) => snapOn ? Math.round(v / GRID_SIZE) * GRID_SIZE : v, [snapOn]);

  const calcGuides = useCallback((dragged, allFields) => {
    const others = allFields.filter((f) => f.id !== dragged.id);
    const lines  = [];
    const dCX = dragged.x + dragged.w / 2, dCY = dragged.y + dragged.h / 2;
    others.forEach((o) => {
      const oCX = o.x + o.w / 2, oCY = o.y + o.h / 2;
      if (Math.abs(dragged.x - o.x) < GUIDE_SNAP)                          lines.push({ dir: 'v', val: o.x });
      if (Math.abs(dCX - oCX) < GUIDE_SNAP)                                lines.push({ dir: 'v', val: oCX });
      if (Math.abs(dragged.x + dragged.w - (o.x + o.w)) < GUIDE_SNAP)     lines.push({ dir: 'v', val: o.x + o.w });
      if (Math.abs(dragged.y - o.y) < GUIDE_SNAP)                          lines.push({ dir: 'h', val: o.y });
      if (Math.abs(dCY - oCY) < GUIDE_SNAP)                                lines.push({ dir: 'h', val: oCY });
      if (Math.abs(dragged.y + dragged.h - (o.y + o.h)) < GUIDE_SNAP)     lines.push({ dir: 'h', val: o.y + o.h });
    });
    return lines;
  }, []);

  const applySnap = useCallback((dragged, allFields) => {
    const others = allFields.filter((f) => f.id !== dragged.id);
    let { x, y } = dragged;
    others.forEach((o) => {
      const oCX = o.x + o.w / 2, oCY = o.y + o.h / 2;
      if (Math.abs(x - o.x) < GUIDE_SNAP)                            x = o.x;
      if (Math.abs(x + dragged.w / 2 - oCX) < GUIDE_SNAP)           x = oCX - dragged.w / 2;
      if (Math.abs(x + dragged.w - (o.x + o.w)) < GUIDE_SNAP)       x = o.x + o.w - dragged.w;
      if (Math.abs(y - o.y) < GUIDE_SNAP)                            y = o.y;
      if (Math.abs(y + dragged.h / 2 - oCY) < GUIDE_SNAP)           y = oCY - dragged.h / 2;
      if (Math.abs(y + dragged.h - (o.y + o.h)) < GUIDE_SNAP)       y = o.y + o.h - dragged.h;
    });
    return { ...dragged, x, y };
  }, []);

  useEffect(() => {
    const img = new Image();
    img.onload = () => { imgRef.current = img; imgSizeRef.current = { w: img.naturalWidth, h: img.naturalHeight }; renderAll(); };
    img.src = currentImage || imageData;
  }, [currentImage, imageData]);

  const renderAll = useCallback(() => {
    const canvas = canvasRef.current, img = imgRef.current;
    if (!canvas || !img) return;
    const containerW = canvas.parentElement.clientWidth;
    const ratio      = containerW / imgSizeRef.current.w;
    const cH         = imgSizeRef.current.h * ratio;
    const { w: iw2src, h: ih2src } = imgSizeRef.current;
    const dpr        = Math.max(window.devicePixelRatio || 1, iw2src / containerW); // 원본해상도 보존
    canvas.width     = containerW * dpr;
    canvas.height    = cH * dpr;
    canvas.style.width  = containerW + 'px';
    canvas.style.height = cH + 'px';
    displaySizeRef.current = { w: containerW, h: cH };
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.scale(dpr, dpr);
    ctx.drawImage(img, 0, 0, containerW, cH);
    const { w: iw, h: ih } = imgSizeRef.current;
    const cW = containerW;
    const toC = (val, dim) => (val / (dim === 'x' ? iw : ih)) * (dim === 'x' ? cW : cH);

    // 그리드
    if (snapOn) {
      ctx.strokeStyle = 'rgba(99,102,241,0.1)'; ctx.lineWidth = 0.5;
      for (let x = 0; x < cW; x += GRID_SIZE * ratio)  { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, cH); ctx.stroke(); }
      for (let y = 0; y < cH; y += GRID_SIZE * ratio) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(cW, y); ctx.stroke(); }
    }

    fields.filter((f) => (f.page || 0) === activePage).forEach((f) => {
      const fx = toC(f.x, 'x'), fy = toC(f.y, 'y'), fw = toC(f.w, 'x'), fh = toC(f.h, 'y');
      const isS = sel === f.id;
      if (f.type === 'radio') {
        const cx2 = fx + fw / 2, cy2 = fy + fh / 2, r = Math.min(fw, fh) / 2;
        ctx.strokeStyle = isS ? '#4f46e5' : '#94a3b8'; ctx.lineWidth = isS ? 2.5 : 1.5;
        ctx.beginPath(); ctx.arc(cx2, cy2, r, 0, Math.PI * 2); ctx.stroke();
        if (isS) { ctx.fillStyle = 'rgba(79,70,229,0.15)'; ctx.beginPath(); ctx.arc(cx2, cy2, r, 0, Math.PI * 2); ctx.fill(); }
      } else if (f.type === 'checkbox') {
        ctx.strokeStyle = isS ? '#4f46e5' : '#374151'; ctx.lineWidth = isS ? 3 : 1.5;
        ctx.strokeRect(fx, fy, fw, fh);
        if (isS) { ctx.fillStyle = 'rgba(79,70,229,0.15)'; ctx.fillRect(fx, fy, fw, fh); }
      } else if (f.type === 'signature') {
        ctx.strokeStyle = isS ? '#4f46e5' : '#94a3b8'; ctx.lineWidth = isS ? 3 : 1; ctx.setLineDash([4, 3]);
        ctx.strokeRect(fx, fy, fw, fh); ctx.setLineDash([]);
        ctx.fillStyle = isS ? 'rgba(79,70,229,0.07)' : 'rgba(148,163,184,0.07)'; ctx.fillRect(fx, fy, fw, fh);
        ctx.fillStyle = '#94a3b8'; ctx.font = `${Math.max(9, fh * 0.35)}px "Pretendard",sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('✍️ ' + (f.label || '서명'), fx + fw / 2, fy + fh / 2); ctx.textAlign = 'left';
      } else {
        ctx.fillStyle = isS ? 'rgba(79,70,229,0.15)' : 'rgba(255,255,255,0.5)';
        ctx.strokeStyle = isS ? '#4f46e5' : '#94a3b8'; ctx.lineWidth = isS ? 3 : 1;
        ctx.beginPath(); ctx.roundRect(fx, fy, fw, fh, 3); ctx.fill(); ctx.stroke();
        ctx.fillStyle = isS ? '#4f46e5' : '#6b7280';
        {
          const lsize = Math.max(8, fh * 0.45);
          ctx.font = `${lsize}px "Pretendard",sans-serif`;
          ctx.textBaseline = 'middle';
          ctx.fillText(f.label || '텍스트', fx + 4, fy + fh / 2);
        }
        ctx.textBaseline = 'alphabetic';
      }
      if (isS) {
        const handles = f.type === 'signature'
          ? [[fx, fy], [fx + fw, fy], [fx, fy + fh], [fx + fw, fy + fh]]
          : f.type === 'text' ? [[fx + fw, fy + fh / 2]] : [];
        handles.forEach(([hx, hy]) => {
          ctx.fillStyle = '#4f46e5'; ctx.beginPath(); ctx.arc(hx, hy, 6, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(hx, hy, 6, 0, Math.PI * 2); ctx.stroke();
        });
      }
    });

    // pendingType 미리보기
    if (pendingTypeRef.current) {
      const { cx, cy } = mousePos.current;
      const { w: iw2, h: ih2 } = imgSizeRef.current;
      const defaults = { text: { w: 30, h: 24 }, date: { w: 60, h: 24 }, checkbox: { w: 20, h: 20 }, signature: { w: 140, h: 56 }, radio: { w: 20, h: 20 } };
      const pd = defaults[pendingTypeRef.current];
      const pw = (pd.w / iw2) * cW, ph = (pd.h / ih2) * cH;
      ctx.save(); ctx.globalAlpha = 0.6;
      ctx.strokeStyle = '#4f46e5'; ctx.lineWidth = 2; ctx.setLineDash([4, 3]);
      ctx.strokeRect(cx - pw / 2, cy - ph / 2, pw, ph);
      ctx.fillStyle = 'rgba(79,70,229,0.1)'; ctx.fillRect(cx - pw / 2, cy - ph / 2, pw, ph);
      ctx.setLineDash([]); ctx.restore();
      ctx.strokeStyle = 'rgba(79,70,229,0.85)'; ctx.lineWidth = 1.5; ctx.setLineDash([5, 3]);
      ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, cH); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(0, cy); ctx.lineTo(cW, cy); ctx.stroke();
      ctx.setLineDash([]);
    }

    // hover 가이드
    if (hoverFid && !drag.current) {
      const hf = fields.find((f) => f.id === hoverFid);
      if (hf) {
        const hx = toC(hf.x, 'x'), hy = toC(hf.y, 'y'), hw = toC(hf.w, 'x'), hh = toC(hf.h, 'y');
        ctx.strokeStyle = 'rgba(6,182,212,0.5)'; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
        [hx, hx + hw, hx + hw / 2].forEach((vx) => { ctx.beginPath(); ctx.moveTo(vx, 0); ctx.lineTo(vx, cH); ctx.stroke(); });
        [hy, hy + hh, hy + hh / 2].forEach((vy) => { ctx.beginPath(); ctx.moveTo(0, vy); ctx.lineTo(cW, vy); ctx.stroke(); });
        ctx.setLineDash([]);
      }
    }

    guides.forEach((g) => {
      ctx.strokeStyle = '#06b6d4'; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
      ctx.beginPath();
      if (g.dir === 'v') { const cx = toC(g.val, 'x'); ctx.moveTo(cx, 0); ctx.lineTo(cx, cH); }
      else               { const cy = toC(g.val, 'y'); ctx.moveTo(0, cy); ctx.lineTo(cW, cy); }
      ctx.stroke(); ctx.setLineDash([]);
    });
  }, [fields, sel, snapOn, guides, hoverFid, activePage]);

  useEffect(() => { renderAll(); }, [renderAll]);

  const FIELD_DEFAULTS = { text: { w: 30, h: 24 }, date: { w: 60, h: 24 }, checkbox: { w: 20, h: 20 }, signature: { w: 140, h: 56 }, radio: { w: 20, h: 20 } };

  const addField = (type, x = 40, y = 40) => {
    const d = FIELD_DEFAULTS[type] || { w: 30, h: 24 };
    if (type === 'radio') {
      const gid = 'rg_' + Date.now();
      setFields((p) => [...p,
        { id: nextId(), type: 'radio', label: '○', x, y, ...d, required: false, groupId: gid, page: activePage },
        { id: nextId(), type: 'radio', label: '○', x: x + d.w * 3, y, ...d, required: false, groupId: gid, page: activePage },
      ]);
      return;
    }
    const label = type === 'text' ? '텍스트' : type === 'date' ? '날짜' : type === 'checkbox' ? '체크박스' : '서명';
    setFields((p) => [...p, { id: nextId(), type, label, x, y, ...d, required: false, maxlength: 100, page: activePage }]);
  };

  const onMouseDown = useCallback((e) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top;
    const { w, h } = imgSizeRef.current;

    const { w: dispW, h: dispH } = displaySizeRef.current;
    if (pendingType) {
      const pd = FIELD_DEFAULTS[pendingType];
      const dx = Math.round((cx / dispW) * w - pd.w / 2);
      const dy = Math.round((cy / dispH) * h - pd.h / 2);
      addField(pendingType, dx, dy);
      return;
    }

    if (sel) {
      const f = fields.find((x) => x.id === sel);
      if (f && f.type !== 'checkbox') {
        const fx = (f.x / w) * dispW, fy = (f.y / h) * dispH;
        const fw = (f.w / w) * dispW, fh = (f.h / h) * dispH;
        const handles = (f.type === 'text' || f.type === 'date')
          ? [['e', fx + fw, fy + fh / 2]]
          : [['nw', fx, fy], ['ne', fx + fw, fy], ['sw', fx, fy + fh], ['se', fx + fw, fy + fh]];
        for (const [corner, hx, hy] of handles) {
          if (Math.abs(cx - hx) < 12 && Math.abs(cy - hy) < 12) {
            drag.current = { type: 'resize', corner, fid: sel, startMx: cx, startMy: cy, startF: { ...f } }; return;
          }
        }
      }
    }

    const pageFields = fields.filter((f) => (f.page || 0) === activePage);
    for (let i = pageFields.length - 1; i >= 0; i--) {
      const f = pageFields[i];
      const fx = (f.x / w) * dispW, fy = (f.y / h) * dispH;
      const fw = (f.w / w) * dispW, fh = (f.h / h) * dispH;
      if (cx >= fx && cx <= fx + fw && cy >= fy && cy <= fy + fh) {
        setSel(f.id);
        drag.current = { type: 'move', fid: f.id, startMx: cx, startMy: cy, startF: { ...f } }; return;
      }
    }
    setSel(null);
  }, [fields, sel, pendingType, activePage]);

  const onMouseMove = useCallback((e) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top;
    const { w, h } = imgSizeRef.current;
    const { w: dispW2, h: dispH2 } = displaySizeRef.current;
    mousePos.current = { cx, cy };

    if (!drag.current) {
      let found = null;
      for (let i = fields.length - 1; i >= 0; i--) {
        const f = fields[i];
        const fx = (f.x / w) * dispW2, fy = (f.y / h) * dispH2;
        const fw = (f.w / w) * dispW2, fh = (f.h / h) * dispH2;
        if (cx >= fx && cx <= fx + fw && cy >= fy && cy <= fy + fh) { found = f.id; break; }
      }
      setHoverFid(found);
      if (pendingTypeRef.current) {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => renderAll());
      }
      return;
    }

    const dx = (cx - drag.current.startMx) / dispW2 * w;
    const dy = (cy - drag.current.startMy) / dispH2 * h;
    const sf = drag.current.startF;

    if (drag.current.type === 'move') {
      let nx = snap(sf.x + dx), ny = snap(sf.y + dy);
      nx = Math.max(0, Math.min(w - sf.w, nx)); ny = Math.max(0, Math.min(h - sf.h, ny));
      const updated = { ...sf, x: nx, y: ny };
      const snapped = applySnap(updated, fields);
      setGuides(calcGuides(snapped, fields));
      const fid = drag.current?.fid; if (!fid) return;
      setFields((prev) => prev.map((f) => f.id === fid ? snapped : f));
    } else if (drag.current.type === 'resize') {
      const c = drag.current.corner;
      let { x, y, w: fw, h: fh } = { ...sf };
      if (sf.type === 'text') {
        fw = Math.max(40, snap(sf.w + dx));
      } else if (sf.type === 'signature') {
        if (c.includes('e'))  fw = Math.max(40, snap(sf.w + dx));
        if (c.includes('s'))  fh = Math.max(20, snap(sf.h + dy));
        if (c.includes('w')) { const nw = Math.max(40, snap(sf.w - dx)); x = sf.x + sf.w - nw; fw = nw; }
        if (c.includes('n')) { const nh = Math.max(20, snap(sf.h - dy)); y = sf.y + sf.h - nh; fh = nh; }
      }
      const fid = drag.current?.fid; if (!fid) return;
      setFields((prev) => prev.map((f) => f.id === fid ? { ...f, x, y, w: fw, h: fh } : f));
    }
  }, [fields, snap, applySnap, calcGuides, renderAll]);

  const onMouseUp = useCallback(() => { drag.current = null; setGuides([]); }, []);

  const onKeyDown = useCallback((e) => {
    const tag = e.target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (e.key === 'Escape') { setPendingType(null); pendingTypeRef.current = null; return; }
    if (!sel) return;
    const step = e.shiftKey ? 1 : 8;
    setFields((prev) => prev.map((f) => {
      if (f.id !== sel) return f;
      if (e.key === 'Delete' || e.key === 'Backspace') { setSel(null); return null; }
      if (e.key === 'ArrowLeft')  return { ...f, x: f.x - step };
      if (e.key === 'ArrowRight') return { ...f, x: f.x + step };
      if (e.key === 'ArrowUp')    return { ...f, y: f.y - step };
      if (e.key === 'ArrowDown')  return { ...f, y: f.y + step };
      return f;
    }).filter(Boolean));
    if (['Delete', 'Backspace', 'Arrow'].some((k) => e.key.includes(k))) e.preventDefault();
  }, [sel]);

  const selField = fields.find((f) => f.id === sel);
  const updateSel = (key, val) => setFields((prev) => prev.map((f) => f.id === sel ? { ...f, [key]: val } : f));

  const saveFields = async () => {
    setSaving(true);
    try {
      await fetch(`${API_BASE}/api/docform/templates/${templateId}/fields`, {
        method: 'PATCH',
        headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields }),
      });
      onSaved(fields);
      setSaveMsg('✅ 저장 완료');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1500, backgroundColor: '#0f172a',
                  display: 'flex', flexDirection: 'column', fontFamily: '"Pretendard",sans-serif' }}
      onKeyDown={onKeyDown} tabIndex={-1}>
      <div style={{ backgroundColor: '#1e293b', borderBottom: '1px solid #334155', padding: '10px 14px',
                    display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        <button onClick={onClose} style={{ padding: '6px 12px', backgroundColor: '#475569', color: '#fff', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '13px' }}>✕ 닫기</button>
        <div style={{ width: '1px', height: '24px', backgroundColor: '#475569' }} />
        {[['text', '✏️ 텍스트'], ['date', '📅 날짜'], ['checkbox', '☑️ 체크'], ['radio', '🔘 라디오'], ['signature', '✍️ 서명']].map(([t, l]) => (
          <button key={t} onClick={() => { const n = pendingType === t ? null : t; setPendingType(n); pendingTypeRef.current = n; }}
            style={{ padding: '6px 12px', backgroundColor: pendingType === t ? '#4f46e5' : '#334155', color: '#e2e8f0',
                     border: pendingType === t ? '2px solid #818cf8' : '1px solid #475569',
                     borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: pendingType === t ? '700' : '400' }}>{l}</button>
        ))}
        <button onClick={() => setSnapOn((p) => !p)}
          style={{ padding: '6px 12px', backgroundColor: snapOn ? '#4f46e5' : '#334155', color: '#fff', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}>
          {snapOn ? '🔲 스냅ON' : '⬛ 스냅OFF'}
        </button>
        <div style={{ flex: 1 }} />
        {pendingType && <span style={{ color: '#06b6d4', fontSize: '12px', fontWeight: '600', marginRight: '8px' }}>📍 클릭해서 배치 (Esc: 취소)</span>}
        {saveMsg && <span style={{ color: '#4ade80', fontSize: '12px', fontWeight: '700', marginRight: '4px' }}>{saveMsg}</span>}
        <button onClick={saveFields} disabled={saving}
          style={{ padding: '7px 18px', backgroundColor: '#059669', color: '#fff', border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '700' }}>
          {saving ? '저장 중...' : '💾 저장'}
        </button>
      </div>
      {allPages && allPages.length > 1 && (
        <div style={{ display: 'flex', gap: '4px', padding: '6px 12px', backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
          {allPages.map((_, i) => (
            <button key={i} onClick={() => { setActivePage(i); setSel(null); }}
              style={{ padding: '4px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
                       backgroundColor: activePage === i ? '#4f46e5' : '#334155', color: activePage === i ? '#fff' : '#94a3b8' }}>
              {i + 1}페이지
            </button>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div style={{ flex: 1, overflow: 'auto', padding: '16px', backgroundColor: '#1a1f2e' }}>
          <canvas ref={canvasRef} style={{ display: 'block', width: '100%', maxWidth: '900px', margin: '0 auto' }}
            onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} />
        </div>
        {selField && (
          <div style={{ width: '220px', backgroundColor: '#1e293b', borderLeft: '1px solid #334155', padding: '14px', overflowY: 'auto', flexShrink: 0 }}>
            <div style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '700', marginBottom: '10px', textTransform: 'uppercase' }}>속성</div>
            {[['label', '라벨'], ['x', 'X'], ['y', 'Y'], ['w', 'W'], ['h', 'H']].map(([k, l]) => (
              <div key={k} style={{ marginBottom: '8px' }}>
                <div style={{ color: '#64748b', fontSize: '10px', marginBottom: '3px' }}>{l}</div>
                <input type={['x', 'y', 'w', 'h'].includes(k) ? 'number' : 'text'} value={selField[k] || ''} onChange={(e) => updateSel(k, ['x', 'y', 'w', 'h'].includes(k) ? Number(e.target.value) : e.target.value)}
                  style={{ width: '100%', padding: '5px 8px', backgroundColor: '#334155', border: '1px solid #475569', borderRadius: '5px', color: '#e2e8f0', fontSize: '12px', boxSizing: 'border-box' }} />
              </div>
            ))}
            {selField.type === 'text' && (
              <div style={{ marginBottom: '8px' }}>
                <div style={{ color: '#64748b', fontSize: '10px', marginBottom: '3px' }}>최대글자</div>
                <input type="number" value={selField.maxlength || 100} onChange={(e) => updateSel('maxlength', Number(e.target.value))}
                  style={{ width: '100%', padding: '5px 8px', backgroundColor: '#334155', border: '1px solid #475569', borderRadius: '5px', color: '#e2e8f0', fontSize: '12px', boxSizing: 'border-box' }} />
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
              <input type="checkbox" id="req" checked={!!selField.required} onChange={(e) => updateSel('required', e.target.checked)} style={{ accentColor: '#4f46e5' }} />
              <label htmlFor="req" style={{ color: '#e2e8f0', fontSize: '12px' }}>필수 항목</label>
            </div>
            <button onClick={() => { setFields((p) => p.filter((f) => f.id !== sel)); setSel(null); }}
              style={{ width: '100%', marginTop: '16px', padding: '7px', backgroundColor: '#7f1d1d', color: '#fca5a5', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}>
              🗑 삭제
            </button>
          </div>
        )}
      </div>
      <div style={{ backgroundColor: '#1e293b', borderTop: '1px solid #334155', padding: '7px 14px', color: '#64748b', fontSize: '11px' }}>
        🖱 드래그: 이동  ·  모서리: 크기조절  ·  방향키: 1px  ·  Shift+방향키: 8px  ·  Del: 삭제  ·  시안색선: 정렬 가이드
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 메인 컴포넌트
// ══════════════════════════════════════════════════════════════
export default function DocFormManager({ token, headers }) {
  const [tab,         setTab]         = useState('library');
  const [templates,   setTemplates]   = useState(() => {
    try { return JSON.parse(localStorage.getItem(`docform_templates_${(token||'').slice(-20)}`) || '[]'); }
    catch { return []; }
  });
  const [loading,     setLoading]     = useState(false);
  const [editor,      setEditor]      = useState(null);
  const [submissions, setSubmissions] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`docform_submissions_${(token||'').slice(-20)}`) || '[]'); }
    catch { return []; }
  });
  const [subLoading,  setSubLoading]  = useState(false);
  const [viewer,      setViewer]      = useState(null);
  const [viewerPages,  setViewerPages]  = useState({});
  const [viewerPageIdx,setViewerPageIdx]= useState(0);
  const [msg,         setMsg]         = useState('');
  const [sendSheet,   setSendSheet]   = useState(null);
  const [sendForm,    setSendForm]    = useState({ room_no: '', user_name: '', phone: '', message: '', kakao_template: '1' });
  const [sending,     setSending]     = useState(false);
  const [residents,   setResidents]   = useState([]);

  // 업로드 상태
  const [upFiles,   setUpFiles]   = useState([]);   // [{file, preview, crop}]
  const [cropIdx,   setCropIdx]   = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragY,   setDragY]   = useState(0);
  const dragStartYRef = useRef(0);
  const ITEM_H = 46;
  const [upTitle,   setUpTitle]   = useState('');
  const [uploading, setUploading] = useState(false);

  const authH = () => headers || { Authorization: `Bearer ${token || localStorage.getItem('auth_token')}` };
  const flash = (m, ms = 2800) => { setMsg(m); setTimeout(() => setMsg(''), ms); };

  // ── 이미지 캐시 훅 사용 ─────────────────────────────────────
  const { cache: imgCache, loading: imgLoading, fetchImage: fetchSubImage } = useImageCache(
    useCallback(async (sid) => {
      const r    = await fetch(`${API_BASE}/api/docform/submissions/${sid}/image?page=0`, { headers: authH() });
      const data = await r.json();
      if (data.total_pages > 1) {
        const pages = [data.data];
        for (let p = 1; p < data.total_pages; p++) {
          const r2 = await fetch(`${API_BASE}/api/docform/submissions/${sid}/image?page=${p}`, { headers: authH() });
          const d2 = await r2.json();
          pages.push(d2.data);
        }
        setViewerPages(prev => ({ ...prev, [sid]: pages }));
      }
      return data.data || null;
    }, [])
  );

  const loadTemplates = async () => {
    setLoading(true);
    const r = await fetch(`${API_BASE}/api/docform/templates`, { headers: authH() });
    if (r.ok) {
      const data = await r.json();
      setTemplates(data);
      try { localStorage.setItem(`docform_templates_${(token||'').slice(-20)}`, JSON.stringify(data)); } catch {}
    }
    setLoading(false);
  };
  useEffect(() => { loadTemplates(); }, []);

  const loadSubmissions = async () => {
    setSubLoading(true);
    const r = await fetch(`${API_BASE}/api/docform/submissions`, { headers: authH() });
    if (r.ok) {
      const data = await r.json();
      setSubmissions(data);
      try { localStorage.setItem(`docform_submissions_${(token||'').slice(-20)}`, JSON.stringify(data)); } catch {}
    }
    setSubLoading(false);
  };

  const deleteSubmission = async (sid) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    const r = await fetch(`${API_BASE}/api/docform/submissions/${sid}`, { method: 'DELETE', headers: authH() });
    if (r.ok) { setSubmissions((prev) => prev.filter((s) => s.id !== sid)); flash('삭제 완료'); }
    else alert('삭제 실패');
  };

  const openSendSheet = async (tmpl) => {
    setSendForm({ room_no: '', user_name: '', phone: '', message: '', kakao_template: '1' });
    setSendSheet(tmpl);
    try {
      const r = await fetch(`${API_BASE}/api/docform/residents`, { headers: authH() });
      if (!r.ok) throw new Error('입주자 목록 로드 실패');
      setResidents(await r.json());
    } catch (e) { alert(e.message || '입주자 목록을 불러올 수 없습니다'); setSendSheet(null); }
  };

  const handleSend = async () => {
    if (!sendForm.room_no || !sendForm.user_name || !sendForm.phone) { alert('호수, 이름, 전화번호를 입력해주세요'); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('room_no',        sendForm.room_no ? sendForm.room_no + '호' : '');
      fd.append('user_name',      sendForm.user_name);
      fd.append('phone',          sendForm.phone);
      fd.append('message',        sendForm.message);
      fd.append('kakao_template', sendForm.kakao_template);
      const r = await fetch(`${API_BASE}/api/docform/templates/${sendSheet.id}/token`, { method: 'POST', headers: authH(), body: fd });
      if (!r.ok) throw new Error('발송 실패');
      setSendSheet(null);
      flash(`📨 "${sendSheet.title}" 발송 완료!`);
    } catch { flash('발송에 실패했습니다'); }
    finally { setSending(false); }
  };

  const openEditor = async (tmpl) => {
    // 모든 페이지 이미지 미리 fetch
    const pages = tmpl.pages || [tmpl.image_path];
    const pageDatas = await Promise.all(pages.map(async p => {
      try { const r2 = await fetch(`${API_BASE}/api/docform/image/${p}`, { headers: authH() }); const d2 = await r2.json(); return d2.data; } catch { return null; }
    }));
    setEditor({ templateId: tmpl.id, imageData: pageDatas[0], allPages: pageDatas, fields: Array.isArray(tmpl.fields) ? tmpl.fields : [] });
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('이 문서를 삭제할까요?')) return;
    await fetch(`${API_BASE}/api/docform/templates/${id}`, { method: 'DELETE', headers: authH() });
    setTemplates((p) => p.filter((t) => t.id !== id));
    flash('삭제되었습니다');
  };

  // 파일 선택 → 페이지 추가
  const handleFileChange = (file, dataUrl) => {
    setUpFiles(prev => [...prev, { file, preview: dataUrl, crop: null }]);
  };
  const handleCropDone = (box) => {
    setUpFiles(prev => prev.map((p, i) => i === cropIdx ? { ...p, crop: box } : p));
    setCropIdx(null);
  };
  const removeUpFile = (idx) => setUpFiles(prev => prev.filter((_, i) => i !== idx));

  const handleUpload = async () => {
    if (!upFiles.length || !upTitle.trim()) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', upTitle.trim());
      upFiles.forEach(p => fd.append('images', p.file));
      fd.append('crops', JSON.stringify(upFiles.map(p => p.crop || null)));
      const r = await fetch(`${API_BASE}/api/docform/templates`, { method: 'POST', headers: authH(), body: fd });
      if (r.ok) {
        flash('업로드 완료!');
        setUpFiles([]); setUpTitle('');
        setTab('library'); loadTemplates();
      } else {
        const err = await r.json().catch(() => ({}));
        flash('업로드 실패 (' + r.status + '): ' + (err.detail || '서버 오류'));
      }
    } catch (e) { flash('네트워크 오류: ' + e.message); }
    finally { setUploading(false); }
  };

  if (editor) return (
    <FieldEditor templateId={editor.templateId} imageData={editor.imageData} allPages={editor.allPages} initialFields={editor.fields} authH={authH}
      onSaved={(newFields) => { setTemplates((prev) => prev.map((t) => t.id === editor.templateId ? { ...t, fields: newFields } : t)); flash('필드 저장 완료'); }}
      onClose={() => setEditor(null)} />
  );

  return (
    <div style={{ fontFamily: '"Pretendard",sans-serif', backgroundColor: '#f1f5f9', minHeight: '100vh', maxWidth: '500px', margin: '0 auto' }}>
      {/* 헤더 */}
      <div style={{ background: 'linear-gradient(135deg,#1a1f2e,#2d3748)', padding: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.25)' }}>
        <div style={{ color: '#fff', fontWeight: '700', fontSize: '16px' }}>📋 문서 전달</div>
        <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '2px', marginBottom: '14px' }}>입주자 서명 요청 시스템</div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {[['library', '📚 라이브러리'], ['submissions', '📨 제출목록'], ['upload', '➕ 업로드']].map(([t, l]) => (
            <button key={t} onClick={() => { setTab(t); if (t === 'submissions') loadSubmissions(); }}
              style={{ padding: '8px 16px', backgroundColor: tab === t ? 'rgba(255,255,255,0.2)' : 'transparent',
                       color: tab === t ? '#fff' : '#94a3b8',
                       border: tab === t ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent',
                       borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: tab === t ? '700' : '400' }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {msg && (
        <div style={{ backgroundColor: '#f0fdf4', borderBottom: '1px solid #86efac', color: '#15803d', padding: '10px 16px', fontSize: '13px', textAlign: 'center', fontWeight: '600' }}>
          {msg}
        </div>
      )}

      {/* 라이브러리 탭 */}
      {tab === 'library' && (
        <div style={{ padding: '14px' }}>
          {loading && <div style={{ color: '#94a3b8', textAlign: 'center', padding: '30px', fontSize: '13px' }}>불러오는 중...</div>}
          {!loading && templates.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: '#94a3b8' }}>
              <div style={{ fontSize: '40px', marginBottom: '10px' }}>📂</div>
              <div style={{ fontSize: '14px', marginBottom: '4px', color: '#64748b' }}>등록된 문서가 없습니다</div>
              <button onClick={() => setTab('upload')}
                style={{ marginTop: '14px', padding: '9px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '9px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
                ➕ 첫 문서 업로드
              </button>
            </div>
          )}
          {templates.map((tmpl) => (
            <div key={tmpl.id} style={{ backgroundColor: '#fff', borderRadius: '14px', marginBottom: '12px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0' }}>
              <div style={{ padding: '12px' }}>
                <div style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b', marginBottom: '4px' }}>{tmpl.title}</div>
                <div style={{ color: '#94a3b8', fontSize: '11px', marginBottom: '12px' }}>
                  필드 {Array.isArray(tmpl.fields) ? tmpl.fields.length : 0}개 · 제출 {tmpl.submission_count || 0}건 · {new Date(tmpl.created_at).toLocaleDateString('ko')}
                </div>
                <div style={{ display: 'flex', gap: '7px' }}>
                  <button onClick={() => openEditor(tmpl)} style={{ flex: 1, padding: '8px', backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>✏️ 필드 편집</button>
                  <button onClick={() => openSendSheet(tmpl)} style={{ flex: 1, padding: '8px', backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>📨 발송</button>
                  <button onClick={() => deleteTemplate(tmpl.id)} style={{ padding: '8px 11px', backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>🗑</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 제출 목록 탭 */}
      {tab === 'submissions' && (
        <div style={{ padding: '14px' }}>
          {subLoading && <div style={{ color: '#94a3b8', textAlign: 'center', padding: '30px', fontSize: '13px' }}>불러오는 중...</div>}
          {!subLoading && submissions.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: '#94a3b8' }}>
              <div style={{ fontSize: '40px', marginBottom: '10px' }}>📭</div>
              <div style={{ fontSize: '14px', color: '#64748b' }}>제출된 문서가 없습니다</div>
            </div>
          )}
          {submissions.map((s) => (
            <div key={s.id} style={{ backgroundColor: '#fff', borderRadius: '10px', marginBottom: '8px', padding: '10px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: '700', fontSize: '12px', color: '#1e293b' }}>
                  {s.doc_title} &nbsp; {s.room_no} {s.user_name}
                </div>
                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                  {new Date(s.submitted_at).toLocaleDateString('ko')} {new Date(s.submitted_at).toLocaleTimeString('ko', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              <button onClick={() => { setViewer(s); fetchSubImage(s.id); setViewerPageIdx(0); }}
                style={{ flexShrink: 0, padding: '6px 10px', backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '7px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>보기</button>
              <button onClick={async () => {
                  try {
                    const r    = await fetch(`${API_BASE}/api/docform/submissions/${s.id}/share`, { method: 'POST', headers: authH() });
                    const data = await r.json();
                    if (!r.ok) { flash('링크 생성 실패'); return; }
                    const link = `${window.location.origin}/docform-view?token=${data.token}`;
                    await navigator.clipboard.writeText(link);
                    flash(`✅ 링크 복사 완료 (~${data.expires_at.slice(0, 10)})`);
                  } catch { flash('링크 생성 실패'); }
                }}
                style={{ flexShrink: 0, padding: '6px 10px', backgroundColor: '#f0fdf4', color: '#059669', border: '1px solid #a7f3d0', borderRadius: '7px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                🔗 공유
              </button>
              <button onClick={() => deleteSubmission(s.id)}
                style={{ flexShrink: 0, padding: '6px 10px', backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '7px', fontSize: '12px', cursor: 'pointer' }}>🗑</button>
            </div>
          ))}
        </div>
      )}

      {/* 업로드 탭 */}
      {tab === 'upload' && (
        <div style={{ padding: '14px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '14px', padding: '18px', boxShadow: '0 2px 10px rgba(0,0,0,0.07)' }}>
            <div style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b', marginBottom: '14px' }}>새 문서 등록</div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '5px', fontWeight: '600' }}>문서 제목</label>
              <input value={upTitle} onChange={(e) => setUpTitle(e.target.value)} placeholder="예: 입주 동의서"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '9px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }} />
            </div>

            {/* 다중 페이지 업로드 */}
            {upFiles.map((p, i) => {
              const isDragging = dragIdx === i;
              let order = i;
              if (dragIdx !== null) {
                const targetIdx = Math.max(0, Math.min(upFiles.length - 1, dragIdx + Math.round(dragY / ITEM_H)));
                if (i === dragIdx) order = targetIdx;
                else if (dragIdx < targetIdx && i > dragIdx && i <= targetIdx) order = i - 1;
                else if (dragIdx > targetIdx && i < dragIdx && i >= targetIdx) order = i + 1;
              }
              const onDragStart = (e) => {
                if (e.target.closest('button')) return;
                dragStartYRef.current = e.touches ? e.touches[0].clientY : e.clientY;
                setDragIdx(i); setDragY(0);
              };
              const onDragMove = (e) => {
                if (dragIdx === null) return;
                const y = e.touches ? e.touches[0].clientY : e.clientY;
                setDragY(y - dragStartYRef.current);
              };
              const onDragEnd = () => {
                if (dragIdx === null) return;
                const targetIdx = Math.max(0, Math.min(upFiles.length - 1, dragIdx + Math.round(dragY / ITEM_H)));
                if (targetIdx !== dragIdx) {
                  setUpFiles(arr => { const a=[...arr]; const [item]=a.splice(dragIdx,1); a.splice(targetIdx,0,item); return a; });
                }
                setDragIdx(null); setDragY(0);
              };
              return (
              <div key={i}
                onMouseDown={onDragStart} onMouseMove={onDragMove} onMouseUp={onDragEnd} onMouseLeave={onDragEnd}
                onTouchStart={onDragStart} onTouchMove={onDragMove} onTouchEnd={onDragEnd}
                style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px',
                         backgroundColor: isDragging ? '#e0e7ff' : '#f8fafc', borderRadius:'10px', padding:'8px 12px',
                         transform: isDragging ? `translateY(${dragY}px)` : `translateY(${(order - i) * ITEM_H}px)`,
                         transition: isDragging ? 'none' : 'transform 0.15s ease',
                         position: 'relative', zIndex: isDragging ? 10 : 1,
                         boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
                         touchAction: 'none', cursor: 'grab', userSelect: 'none' }}>
                <span style={{ fontSize:'14px', color:'#94a3b8' }}>☰</span>
                <span style={{ fontSize:'12px' }}>📄</span>
                <div style={{ flex:1, fontSize:'12px', color:'#374151', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {p.file?.name || `이미지 ${i+1}`}{p.crop ? ' ✅' : ''}
                </div>
                <button onClick={() => setCropIdx(i)}
                  style={{ padding:'4px 8px', backgroundColor:'#eff6ff', color:'#2563eb', border:'1px solid #bfdbfe', borderRadius:'6px', fontSize:'11px', cursor:'pointer' }}>✂️</button>
                <button onClick={() => removeUpFile(i)}
                  style={{ padding:'4px 8px', backgroundColor:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca', borderRadius:'6px', fontSize:'11px', cursor:'pointer' }}>✕</button>
              </div>
              );
            })}
            <ImageUploadBox
              label={upFiles.length ? `페이지 ${upFiles.length + 1} 추가` : "문서 이미지"}
              icon="📄"
              preview={null}
              hasCrop={false}
              onFileChange={handleFileChange}
              onCropClick={() => {}}
            />

            <button onClick={handleUpload} disabled={uploading || !upFiles.length || !upTitle.trim()}
              style={{ width: '100%', padding: '12px', backgroundColor: (uploading || !upFiles.length || !upTitle.trim()) ? '#94a3b8' : '#4f46e5',
                       color: '#fff', border: 'none', borderRadius: '10px',
                       cursor: (uploading || !upFiles.length || !upTitle.trim()) ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '700' }}>
              {uploading ? '업로드 중...' : `📤 업로드 (${upFiles.length}페이지)`}
            </button>
          </div>
        </div>
      )}

      {/* 이미지 뷰어 — 공통 컴포넌트 사용 */}
      {viewer && (
        <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.9)', zIndex:1000, display:'flex', flexDirection:'column' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 16px', backgroundColor:'#1f2937', flexShrink:0 }}>
            <div>
              <span style={{ color:'#fff', fontWeight:'bold', fontSize:'14px' }}>{viewer.doc_title}  {viewer.room_no} {viewer.user_name}</span>
            </div>
            <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
              <button onClick={() => downloadAllPdf(viewerPages[viewer.id] || [imgCache[viewer.id]], `${viewer.doc_title}_${viewer.room_no}_${viewer.user_name}`)}
                style={{ padding:'6px 12px', backgroundColor:'#dc2626', color:'#fff', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'bold', cursor:'pointer' }}>
                📄 PDF
              </button>
              <button onClick={() => setViewer(null)}
                style={{ background:'none', border:'none', color:'#fff', fontSize:'20px', cursor:'pointer' }}>✕</button>
            </div>
          </div>
          <div style={{ flex:1, overflowY:'auto', padding:'12px', display:'flex', flexDirection:'column', gap:'12px', alignItems:'center' }}>
            {(viewerPages[viewer.id] || [imgCache[viewer.id]]).filter(Boolean).map((img, i, arr) => (
              <div key={i} style={{ width:'100%', maxWidth:'800px' }}>
                <img src={img} alt={`p${i+1}`} draggable={false}
                  onContextMenu={e => e.preventDefault()}
                  style={{ width:'100%', borderRadius:'8px', display:'block', userSelect:'none', WebkitUserSelect:'none' }} />
                {i < arr.length - 1 && <div style={{ height:'1px', backgroundColor:'#374151', margin:'12px 0' }} />}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 크롭 툴 — 공통 컴포넌트 사용 */}
      {cropIdx !== null && upFiles[cropIdx] && (
        <CropTool
          src={upFiles[cropIdx].preview}
          onDone={handleCropDone}
          onCancel={() => setCropIdx(null)}
        />
      )}

      {/* 발송 바텀시트 */}
      {sendSheet && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={(e) => { if (e.target === e.currentTarget) setSendSheet(null); }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px 36px', width: '100%', maxWidth: '500px', boxSizing: 'border-box' }}>
            <div style={{ textAlign: 'center', marginBottom: '18px' }}>
              <div style={{ width: '36px', height: '4px', backgroundColor: '#e2e8f0', borderRadius: '2px', margin: '0 auto 16px' }} />
              <div style={{ fontWeight: '700', fontSize: '16px', color: '#1e293b' }}>📨 문서 발송</div>
              <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>{sendSheet.title}</div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px', fontWeight: '600' }}>알림톡 템플릿</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[['1', '퇴실확인'], ['2', '계약갱신'], ['3', '공지']].map(([v, l]) => (
                  <button key={v} onClick={() => setSendForm((f) => ({ ...f, kakao_template: v }))}
                    style={{ flex: 1, padding: '9px 4px', borderRadius: '9px', border: '2px solid',
                             borderColor: sendForm.kakao_template === v ? '#4f46e5' : '#e2e8f0',
                             backgroundColor: sendForm.kakao_template === v ? '#eef2ff' : '#fff',
                             color: sendForm.kakao_template === v ? '#4f46e5' : '#64748b',
                             fontSize: '12px', fontWeight: sendForm.kakao_template === v ? '700' : '400', cursor: 'pointer' }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', fontWeight: '600' }}>호수</div>
              <select value={sendForm.room_no} onChange={(e) => {
                  const r = residents.find((r) => r.room_no === e.target.value);
                  setSendForm((f) => ({ ...f, room_no: e.target.value, user_name: r?.user_name || '', phone: r?.phone || '' }));
                }}
                style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box', backgroundColor: '#fff', outline: 'none' }}>
                <option value="">호수 선택</option>
                {residents.map((r) => <option key={r.room_no} value={r.room_no}>{r.room_no}</option>)}
              </select>
            </div>

            {[['user_name', '이름', '이름 입력', 20], ['phone', '전화번호', '010-0000-0000', 13]].map(([key, label, ph, max]) => (
              <div key={key} style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', fontWeight: '600' }}>{label}</div>
                <input value={sendForm[key]} onChange={(e) => setSendForm((f) => ({ ...f, [key]: e.target.value }))}
                  placeholder={ph} maxLength={max} type={key === 'phone' ? 'tel' : 'text'}
                  style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box', outline: 'none', backgroundColor: sendForm[key] ? '#f0fdf4' : '#fff' }} />
              </div>
            ))}

            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', fontWeight: '600' }}>
                안내문구 <span style={{ fontWeight: '400' }}>(선택)</span>
              </div>
              <textarea value={sendForm.message} onChange={(e) => setSendForm((f) => ({ ...f, message: e.target.value }))}
                placeholder="입주자에게 전달할 안내 내용을 입력하세요" maxLength={200} rows={3}
                style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '13px', boxSizing: 'border-box', outline: 'none', resize: 'none', fontFamily: 'inherit' }} />
            </div>

            <button onClick={handleSend} disabled={sending}
              style={{ width: '100%', padding: '14px', backgroundColor: sending ? '#94a3b8' : '#4f46e5', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: '700', fontSize: '15px', cursor: sending ? 'not-allowed' : 'pointer' }}>
              {sending ? '📨 발송 중...' : '📨 발송하기'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
